/*
 * Intelligent Fabric — themed placeholder for operator surfaces that become
 * real plugin pages in later phases (Policy Explorer, NATS inspector,
 * Compliance scorecards, Identity view, HITL Approvals).
 *
 * Keeps the sidebar honest: every surface renders an intentional "coming in
 * Phase N" page with live deep-links to today's equivalent (Grafana / ArgoCD /
 * the catalog) instead of a dead route. Swap each for its plugin page as it
 * ships — see MIGRATION.md §4.
 */
import {
  Content,
  ContentHeader,
  Header,
  Page,
  InfoCard,
  Link,
  EmptyState,
} from '@backstage/core-components';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

export type SurfaceLink = { title: string; to: string; external?: boolean };

export type SurfacePlaceholderProps = {
  title: string;
  subtitle: string;
  phase: string;
  description: string;
  links?: SurfaceLink[];
};

export const SurfacePlaceholder = ({
  title,
  subtitle,
  phase,
  description,
  links = [],
}: SurfacePlaceholderProps) => (
  <Page themeId="tool">
    <Header title={title} subtitle={subtitle} />
    <Content>
      <ContentHeader title={`Coming in ${phase}`} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <InfoCard title="What this surface will do">
            <Typography variant="body1" paragraph>
              {description}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Delivered as a custom Backstage plugin page in {phase}. Until then,
              use the live links to the right.
            </Typography>
          </InfoCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <InfoCard title="Use today">
            {links.length === 0 ? (
              <EmptyState
                missing="content"
                title="No interim view"
                description="This capability has no standalone equivalent yet."
              />
            ) : (
              links.map(l => (
                <Typography key={l.to} variant="body2" gutterBottom>
                  <Link to={l.to} {...(l.external ? { target: '_blank' } : {})}>
                    {l.title} →
                  </Link>
                </Typography>
              ))
            )}
          </InfoCard>
        </Grid>
      </Grid>
    </Content>
  </Page>
);

export default SurfacePlaceholder;
